import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import type { Board } from '../../lib/api';
import { cn } from '../../ui/cn';
import { BulkBar } from './BulkBar';
import type { ItemMenu } from './ItemMenu';
import type { BoardActionId } from './model/actions';
import { useDashboardKeys } from './useDashboardKeys';
import { useBulk } from './BulkBar';
import { usePaletteCommands } from '../palette/registry';
import { useTranslation } from 'react-i18next';
import { EMPTY_SELECTION, prune, select, selectRect, type Selection } from './model/selection';

type Modifiers = { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean };

export type SelectionApi = {
  selection: Selection;
  /** A click on a card: Shift = range in the page's visual order, Cmd/Ctrl = toggle, `toggle` = the card's checkbox. */
  click: (id: string, mods: Modifiers, toggle?: boolean) => void;
  set: (sel: Selection) => void;
  clear: () => void;
  /** Ids of the board cards of the page, in visual order. */
  order: () => string[];
  /** Board grids declare what they show; the selection keeps only that (a filter or a move prunes it). */
  register: (key: string, boards: readonly Board[]) => void;
  unregister: (key: string) => void;
  /** Each card's menu, for the keyboard (F2, Delete on the focused card). */
  menus: Map<string, ItemMenu<BoardActionId>>;
  /** Every board the page shows (drag and drop of a selection). */
  boards: () => Board[];
};

const SelectionContext = createContext<SelectionApi | null>(null);
export const useSelection = () => useContext(SelectionContext);

/** Below this many pixels a press on empty space is a click (it clears), above it draws a selection rectangle. */
const RECT_THRESHOLD = 4;
const NOT_EMPTY =
  'a, button, input, textarea, select, label, [role="checkbox"], [role="menu"], [role="dialog"], [role="toolbar"], [role="group"], [data-board-id]';

/**
 * One selection per dashboard page (P16-4 Ruling 4): every board grid inside joins it, the bulk bar shows while
 * something is selected, and a press-and-drag on empty space (mouse only) draws a selection rectangle.
 */
export function SelectionScope({ children }: { children: ReactNode }) {
  const root = useRef<HTMLDivElement>(null);
  const [selection, setSelection] = useState<Selection>(EMPTY_SELECTION);
  const grids = useRef(new Map<string, readonly Board[]>());
  const menus = useRef(new Map<string, ItemMenu<BoardActionId>>());
  const [shown, setShown] = useState('');
  const [rect, setRect] = useState<DOMRect | null>(null);

  const order = useCallback(() => [...(root.current?.querySelectorAll<HTMLElement>('[data-board-id]') ?? [])].map((el) => el.dataset.boardId!), []);
  const sync = useCallback(() => setShown([...grids.current.values()].flatMap((list) => list.map((b) => b.id)).join(',')), []);
  const register = useCallback(
    (key: string, boards: readonly Board[]) => {
      grids.current.set(key, boards);
      sync();
    },
    [sync],
  );
  const unregister = useCallback(
    (key: string) => {
      grids.current.delete(key);
      sync();
    },
    [sync],
  );
  const clear = useCallback(() => setSelection(EMPTY_SELECTION), []);
  const click = useCallback<SelectionApi['click']>(
    (id, mods, toggle = false) => setSelection((s) => select(s, id, order(), { shift: mods.shiftKey, toggle: toggle || mods.metaKey || mods.ctrlKey })),
    [order],
  );
  const boards = useCallback(() => [...grids.current.values()].flat(), []);
  const api = useMemo<SelectionApi>(
    () => ({ selection, click, set: setSelection, clear, order, register, unregister, menus: menus.current, boards }),
    [selection, click, clear, order, register, unregister, boards],
  );

  useEffect(() => {
    setSelection((s) => prune(s, shown ? shown.split(',') : []));
  }, [shown]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    if (e.target instanceof Element && e.target.closest(NOT_EMPTY)) return;
    const start = { x: e.clientX, y: e.clientY };
    const add = e.shiftKey || e.metaKey || e.ctrlKey;
    const base = selection;
    let drawing = false;
    const boxTo = (x: number, y: number) => new DOMRect(Math.min(start.x, x), Math.min(start.y, y), Math.abs(x - start.x), Math.abs(y - start.y));
    const move = (ev: PointerEvent) => {
      if (!drawing && Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < RECT_THRESHOLD) return;
      drawing = true;
      const box = boxTo(ev.clientX, ev.clientY);
      setRect(box);
      const rects = new Map([...(root.current?.querySelectorAll<HTMLElement>('[data-board-id]') ?? [])].map((el) => [el.dataset.boardId!, el.getBoundingClientRect()]));
      setSelection(selectRect(rects, box, base, add));
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      setRect(null);
      if (!drawing && !add) setSelection(EMPTY_SELECTION);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  };

  useDashboardKeys(root, { selection, set: setSelection, clear, order, boards, menus: menus.current });
  useSelectionCommands(selection, boards, menus.current, clear);

  return (
    <SelectionContext.Provider value={api}>
      <div ref={root} data-selection-scope onPointerDown={onPointerDown} className={cn('flex flex-col gap-10', rect && 'select-none')}>
        {children}
      </div>
      {rect && (
        <div
          data-testid="selection-rect"
          aria-hidden
          className="pointer-events-none fixed z-30 rounded-sm border border-accent bg-accent/10"
          style={{ left: rect.x, top: rect.y, width: rect.width, height: rect.height }}
        />
      )}
      {selection.ids.size > 0 && <BulkBar boards={boards().filter((b) => selection.ids.has(b.id))} onClear={api.clear} />}
    </SelectionContext.Provider>
  );
}

/** A grid's boards join the page's selection for as long as the grid is shown. */
export function useRegisterBoards(key: string, boards: readonly Board[]) {
  const scope = useSelection();
  const signature = boards.map((b) => `${b.id}:${b.updatedAt}:${b.projectId}:${b.folderId}:${b.archivedAt}`).join('|');
  const register = scope?.register;
  const unregister = scope?.unregister;
  // Re-registers only when what the grid shows changes (`signature`), not on every render.
  useLayoutEffect(() => register?.(key, boards), [register, key, signature]);
  useLayoutEffect(() => () => unregister?.(key), [unregister, key]);
}

/** A card's menu is reachable from the keyboard while the card is shown. */
export function useRegisterMenu(id: string, menu: ItemMenu<BoardActionId>) {
  const menus = useSelection()?.menus;
  useLayoutEffect(() => {
    menus?.set(id, menu);
  });
  useLayoutEffect(() => () => void menus?.delete(id), [menus, id]);
}

/** ⌘K commands on the selection: rename (one board), archive and trash (all it may act on). */
function useSelectionCommands(selection: Selection, boards: () => Board[], menus: ReadonlyMap<string, ItemMenu<BoardActionId>>, clear: () => void) {
  const { t } = useTranslation();
  const bulk = useBulk();
  usePaletteCommands(() => {
    const selected = boards().filter((b) => selection.ids.has(b.id));
    if (selected.length === 0) return [];
    const allowed = bulk.allowedFor(selected);
    const ids = (id: 'archive' | 'trash') => allowed.find((a) => a.id === id)!.allowedIds;
    const one = selected.length === 1 ? menus.get(selected[0]!.id) : undefined;
    return [
      ...(one?.entries.some((e) => e.state.id === 'rename' && e.state.allowed)
        ? [{ id: 'selection:rename', label: t('dashboard.keys.renameSelection'), group: 'board' as const, run: () => one.run('rename') }]
        : []),
      ...(['archive', 'trash'] as const)
        .filter((id) => ids(id).length > 0)
        .map((id) => ({
          id: `selection:${id}`,
          label: t(`dashboard.keys.${id}Selection`),
          group: 'board' as const,
          run: () => void bulk.run(id, selected, ids(id)).then(clear, () => {}),
        })),
    ];
  });
}
